/**
 * ActorsPage — Wave E + Wave H + Milestone A (AC-ACTORS-1/3)
 *
 * Milestone A (AC-ACTORS-1): Three create buttons — Human · AI Operator · Knowledge Persona.
 *   New Human button added left of the other two (canManageActors gated).
 * Milestone A (AC-ACTORS-3): Single filter row L→R: Actor type · Name · Domain · Adoption level · Show-inactive.
 * Wave H (AC-NAV-5): Create buttons split; initialActorType pre-selects type in create mode.
 * Wave E — AC-EDIT-1 finalize: ALL actor types open UnifiedActorEditor.
 * Wave E — AC-EDIT-4: View button removed; actor card opens editor directly.
 */
import React, { useMemo, useState } from 'react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { useRorosActors, useRorosActorDeactivate, useRorosActorReactivate, useRorosActorArchive } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosAgents } from '@/hooks/roros/agents';
import { usePermissions } from '@/hooks/usePermissions';
import { RorosActorType } from '@/types/roros/enums';
import { UnifiedActorEditor } from './UnifiedActorEditor';
import { Users, Plus, Power, PowerOff, Pencil, User, Bot, BookOpen, Trash2, ShieldCheck } from 'lucide-react';
import { Switch } from '@/components/ui/Switch';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosAgentDefinitionDTO } from '@/services/roros/ai-operators.service';
import { ActorAvatar } from '@/components/roros/ActorAvatar';

type FilterActorType = 'all' | 'human' | 'ai_operator' | 'knowledge_persona';

function actorTypeBadge(type: string) {
  switch (type) {
    case RorosActorType.HUMAN:
      return (
        <Badge className="bg-blue-100 text-blue-700">
          <User className="h-3 w-3 mr-1" />
          {t('Human')}
        </Badge>
      );
    case RorosActorType.AI_OPERATOR:
      return (
        <Badge className="bg-purple-100 text-purple-700">
          <Bot className="h-3 w-3 mr-1" />
          {t('AI Operator')}
        </Badge>
      );
    case RorosActorType.KNOWLEDGE_PERSONA:
      return (
        <Badge variant="warning">
          <BookOpen className="h-3 w-3 mr-1" />
          {t('Knowledge Persona')}
        </Badge>
      );
    default:
      return <Badge variant="secondary">{type}</Badge>;
  }
}

function adoptionLevelBadge(level: string) {
  switch (level) {
    case 'shadow':
      return <Badge variant="secondary">{t('Shadow')}</Badge>;
    case 'copilot':
      return <Badge className="bg-blue-100 text-blue-700">{t('Copilot')}</Badge>;
    case 'autopilot':
      return <Badge variant="success">{t('Autopilot')}</Badge>;
    default:
      return <Badge variant="secondary">{level}</Badge>;
  }
}

/**
 * ActorsPanel — content only, used in the Structure Settings "Actors" tab.
 * The ActorsPage export below wraps this in RorosPage for the legacy
 * /roros/actors route.
 */
export function ActorsPanel() {
  const actorsQuery = useRorosActors();
  const domainsQuery = useRorosDomains();
  const agentsQuery = useRorosAgents();
  const deactivateMutation = useRorosActorDeactivate();
  const reactivateMutation = useRorosActorReactivate();
  const archiveMutation = useRorosActorArchive();
  const { hasPermission } = usePermissions();
  const canManageActors = hasPermission('roros.actors.manage');

  // Map actor.id → agent_definition for ai_operators
  const definitionByActorId = useMemo(() => {
    const map = new Map<string, RorosAgentDefinitionDTO>();
    for (const def of (agentsQuery.data as RorosAgentDefinitionDTO[] | undefined) ?? []) {
      if (def.actorId) map.set(def.actorId, def);
    }
    return map;
  }, [agentsQuery.data]);

  // AC-ACTORS-3: filter row state — actor type · name · domain · adoption · show-inactive
  const [actorTypeFilter, setActorTypeFilter] = useState<FilterActorType>('all');
  const [nameSearch, setNameSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('any');
  const [adoptionFilter, setAdoptionFilter] = useState('any');
  const [showInactive, setShowInactive] = useState(false);

  // Wave E: ONE editor target for ALL actor types (AC-EDIT-1 finalize)
  // Wave H (AC-NAV-5): initialActorType used to pre-select type in create mode
  // Milestone A (AC-ACTORS-1): three explicit create buttons; human is first
  const [editorTarget, setEditorTarget] = useState<{
    actor: RorosActorDTO | null;
    agentDefinition: RorosAgentDefinitionDTO | null;
    isCreate?: boolean;
    initialActorType?: string;
  } | null>(null);
  const [deactivateActor, setDeactivateActor] = useState<RorosActorDTO | null>(null);
  const [archiveActor, setArchiveActor] = useState<RorosActorDTO | null>(null);

  const domainMap = useMemo(() => {
    const map = new Map<string, string>();
    if (domainsQuery.data) {
      for (const d of domainsQuery.data) {
        map.set(d.id, d.name);
      }
    }
    return map;
  }, [domainsQuery.data]);

  // Active domain IDs for filtering card pills — inactive domains must NOT appear (MC refinement)
  const activeDomainIdSet = useMemo(() => {
    const set = new Set<string>();
    for (const d of domainsQuery.data ?? []) {
      if (d.isActive) set.add(d.id);
    }
    return set;
  }, [domainsQuery.data]);

  const inactiveCount = useMemo(
    () => (actorsQuery.data ?? []).filter((a) => a.status !== 'active').length,
    [actorsQuery.data],
  );

  // AC-ACTORS-3: Filters compose (AND): type · name (substring/case-insensitive) · domain · adoption · inactive
  const filteredActors = useMemo(() => {
    if (!actorsQuery.data) return [];
    let rows = actorsQuery.data;
    if (!showInactive) rows = rows.filter((a) => a.status === 'active');
    if (actorTypeFilter !== 'all') rows = rows.filter((a) => a.actorType === actorTypeFilter);
    if (nameSearch.trim()) {
      const q = nameSearch.trim().toLowerCase();
      rows = rows.filter((a) => (a.name ?? '').toLowerCase().includes(q));
    }
    if (domainFilter && domainFilter !== 'any') {
      rows = rows.filter((a) => a.domains?.some((d) => d.domainId === domainFilter));
    }
    if (adoptionFilter && adoptionFilter !== 'any') {
      rows = rows.filter((a) => a.adoptionLevel === adoptionFilter);
    }
    return rows;
  }, [actorsQuery.data, actorTypeFilter, nameSearch, domainFilter, adoptionFilter, showInactive]);

  const handleDeactivateConfirm = () => {
    if (!deactivateActor) return;
    deactivateMutation.mutate(deactivateActor.id, {
      onSuccess: () => {
        showToast.success(t('Actor deactivated'));
        setDeactivateActor(null);
      },
      onError: (err) => {
        showToast.error(err instanceof Error ? err.message : t('Failed to deactivate actor'));
        setDeactivateActor(null);
      },
    });
  };

  const handleArchiveConfirm = () => {
    if (!archiveActor) return;
    archiveMutation.mutate(archiveActor.id, {
      onSuccess: () => {
        showToast.success(t('Actor deleted'));
        setArchiveActor(null);
      },
      onError: (err) => {
        showToast.error(err instanceof Error ? err.message : t('Failed to delete actor'));
        setArchiveActor(null);
      },
    });
  };

  const handleReactivate = (actor: RorosActorDTO) => {
    reactivateMutation.mutate(actor.id, {
      onSuccess: () => showToast.success(t('Actor reactivated')),
      onError: (err) =>
        showToast.error(err instanceof Error ? err.message : t('Failed to reactivate actor')),
    });
  };

  const openEditorForActor = (actor: RorosActorDTO) => {
    setEditorTarget({
      actor,
      agentDefinition: actor.actorType === RorosActorType.AI_OPERATOR
        ? (definitionByActorId.get(actor.id) ?? null)
        : null,
    });
  };

  // AC-ACTORS-3: dropdown options for filters
  const actorTypeOptions = [
    { value: 'all', label: t('All types') },
    { value: 'human', label: t('Human') },
    { value: 'ai_operator', label: t('AI Operator') },
    { value: 'knowledge_persona', label: t('Knowledge Persona') },
  ];

  const adoptionOptions = [
    { value: 'any', label: t('Any adoption') },
    { value: 'shadow', label: t('Shadow') },
    { value: 'copilot', label: t('Copilot') },
    { value: 'autopilot', label: t('Autopilot') },
  ];

  // Finding 3: Domain filter must list ONLY active domains (+ "Any domain")
  const domainOptions = useMemo(() => {
    const base = [{ value: 'any', label: t('Any domain') }];
    const domains = (domainsQuery.data ?? []).filter((d) => d.isActive);
    return [
      ...base,
      ...domains.map((d) => ({ value: d.id, label: d.name })),
    ];
  }, [domainsQuery.data]);

  return (
    <div>
      {/* AC-ACTORS-1: Three create buttons — Human · AI Operator · Knowledge Persona (canManageActors gated) */}
      {canManageActors && (
        <div className="flex items-center justify-end gap-2 mb-4">
          <Button
            onClick={() => setEditorTarget({ actor: null, agentDefinition: null, isCreate: true, initialActorType: 'human' })}
            data-testid="actors-new-human-button"
            variant="outline"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('New Human')}
          </Button>
          <Button
            onClick={() => setEditorTarget({ actor: null, agentDefinition: null, isCreate: true, initialActorType: 'ai_operator' })}
            data-testid="actors-new-ai-operator-button"
            variant="outline"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('New AI Operator')}
          </Button>
          <Button
            onClick={() => setEditorTarget({ actor: null, agentDefinition: null, isCreate: true, initialActorType: 'knowledge_persona' })}
            data-testid="actors-new-persona-button"
            variant="outline"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('New Knowledge Persona')}
          </Button>
        </div>
      )}

      {/* AC-ACTORS-3: Single filter row L→R: Actor type · Name · Domain · Adoption level · Show-inactive
          flex-wrap for mobile (narrow screens); lg:flex-nowrap forces ONE row at desktop width (≥1024px).
          PART-A polish: Name input wrapper is flex-1 to absorb dead gap; Select wrappers have explicit
          widths (not w-full) so labels never wrap; show-inactive has no ml-auto (flex-1 name input
          pushes it to the right edge naturally). */}
      <div
        className="flex flex-wrap lg:flex-nowrap items-center gap-3 mb-6 p-3 bg-muted/30 rounded-lg border"
        data-testid="actors-filter-row"
      >
        {/* Actor type filter — w-[200px] so "Knowledge Persona" fits on one line without wrap */}
        <div className="w-[200px] shrink-0">
          <Select
            options={actorTypeOptions}
            value={actorTypeFilter}
            onValueChange={(v) => setActorTypeFilter(v as FilterActorType)}
            aria-label={t('Filter by actor type')}
            data-testid="actors-filter-type"
          />
        </div>

        {/* Name search — flex-1 absorbs dead centre gap; min-w-[120px] keeps it usable but narrower */}
        <div className="flex-1 min-w-[120px]">
          <Input
            type="search"
            placeholder={t('Search by name…')}
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            aria-label={t('Search actors by name')}
            data-testid="actors-filter-name"
          />
        </div>

        {/* Domain filter — fixed width so "Any domain" never wraps */}
        <div className="w-[160px] shrink-0">
          <Select
            options={domainOptions}
            value={domainFilter}
            onValueChange={(v) => setDomainFilter(v)}
            aria-label={t('Filter by domain')}
            data-testid="actors-filter-domain"
          />
        </div>

        {/* Adoption level filter — fixed width so "Any adoption" never wraps */}
        <div className="w-[160px] shrink-0">
          <Select
            options={adoptionOptions}
            value={adoptionFilter}
            onValueChange={(v) => setAdoptionFilter(v)}
            aria-label={t('Filter by adoption level')}
            data-testid="actors-filter-adoption"
          />
        </div>

        {/* Show-inactive — flush right (no ml-auto; the flex-1 name input pushes it there) */}
        <label className="flex items-center gap-2 text-sm shrink-0">
          <Switch
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            aria-label={t('Show inactive actors')}
            data-testid="actors-show-inactive-toggle"
          />
          <span>
            {t('Show inactive')}
            {inactiveCount > 0 && (
              <span className="ml-1 text-muted-foreground">({inactiveCount})</span>
            )}
          </span>
        </label>
      </div>

      <QueryPage
        query={{ ...actorsQuery, data: filteredActors }}
        emptyMessage={t('No actors found')}
        emptyAction={
          canManageActors ? (
            <Button onClick={() => setEditorTarget({ actor: null, agentDefinition: null, isCreate: true, initialActorType: 'ai_operator' })}
              data-testid="actors-empty-new-ai-operator-button">
              <Plus className="h-4 w-4 mr-1" />
              {t('New AI Operator')}
            </Button>
          ) : undefined
        }
      >
        {(actors) => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {actors.map((actor) => {
              const isActive = actor.status === 'active';
              const isProtected = !!(actor.systemRole);
              // Item 3: background tint by actor type for at-a-glance visual differentiation.
              // The Card component applies `bg-card` via cn()/tailwind-merge; tailwind-merge does NOT
              // dedupe the semantic token `bg-card` against `bg-info-100` (they're treated as separate
              // groups), so BOTH classes stay and `bg-card` can win the CSS cascade.
              // Using the Tailwind `!` important modifier (`!bg-*`) guarantees the tint overrides.
              // `primary`/`secondary` are brand-overridden to grays on this client; use `info` (blue),
              // `success` (green), and `warning` (amber) which have distinct, non-overridden values.
              const cardTint = actor.actorType === RorosActorType.HUMAN
                ? '!bg-info-100 border-l-4 border-info-400'
                : actor.actorType === RorosActorType.AI_OPERATOR
                  ? '!bg-success-100 border-l-4 border-success-400'
                  : actor.actorType === RorosActorType.KNOWLEDGE_PERSONA
                    ? '!bg-warning-100 border-l-4 border-warning-400'
                    : '';
              return (
                <Card
                  key={actor.id}
                  padding="none"
                  className={`h-full hover:shadow-md transition-shadow ${!isActive ? 'opacity-60 border-dashed' : ''} ${cardTint}`}
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* AC-EDIT-4: actor name is now a button opening the editor (no Link to detail page) */}
                    <div className="flex items-start gap-3 mb-3">
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex items-start gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left p-0 h-auto"
                        onClick={() => openEditorForActor(actor)}
                        data-testid={`actor-card-open-${actor.id}`}
                      >
                        <ActorAvatar actor={actor} size="md" className="mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-base truncate hover:underline">
                            {actor.name}
                          </div>
                        </div>
                      </Button>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge variant={isActive ? 'success' : 'secondary'}>
                          {isActive ? t('Active') : t('Inactive')}
                        </Badge>
                        {/* AC-MODEL-5: System badge for protected actors */}
                        {isProtected && (
                          <Badge
                            className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1 text-xs"
                            data-testid={`actor-system-badge-${actor.id}`}
                          >
                            <ShieldCheck className="h-3 w-3" />
                            {t('System')}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {actorTypeBadge(actor.actorType)}
                      {adoptionLevelBadge(actor.adoptionLevel)}
                    </div>

                    {actor.expertiseScope && (
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                        {actor.expertiseScope}
                      </p>
                    )}

                    {actor.domains && actor.domains.length > 0 && (() => {
                      const activePills = actor.domains.filter((d) => activeDomainIdSet.has(d.domainId));
                      return activePills.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {activePills.map((d) => (
                            <Badge key={d.domainId} variant="outline" className="text-xs">
                              {domainMap.get(d.domainId) ?? d.domainId}
                            </Badge>
                          ))}
                        </div>
                      ) : null;
                    })()}

                    {/* Action row — Wave E: no View button; Edit opens unified editor for all types */}
                    <div className="flex items-center justify-end gap-2 mt-auto pt-6 border-t">
                      {canManageActors && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditorForActor(actor)}
                            data-testid={`actor-edit-${actor.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            {t('Edit')}
                          </Button>
                          {/* AC-HUMAN-3 (Wave I): Deactivate/Delete hidden for human actors.
                              Humans' lifecycle is managed via User management, not actor controls. */}
                          {actor.actorType !== RorosActorType.HUMAN && (
                            <>
                              {isActive ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setDeactivateActor(actor)}
                                  disabled={deactivateMutation.isPending || isProtected}
                                  title={isProtected ? t('Cannot deactivate system actors') : t('Deactivate')}
                                  aria-label={t('Deactivate')}
                                  data-testid={`actor-deactivate-${actor.id}`}
                                >
                                  <PowerOff className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReactivate(actor)}
                                  disabled={reactivateMutation.isPending}
                                  title={t('Reactivate')}
                                  aria-label={t('Reactivate')}
                                  data-testid={`actor-reactivate-${actor.id}`}
                                >
                                  <Power className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {!isProtected && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setArchiveActor(actor)}
                                  disabled={archiveMutation.isPending}
                                  className="text-destructive hover:text-destructive"
                                  title={t('Delete')}
                                  aria-label={t('Delete')}
                                  data-testid={`actor-delete-${actor.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </QueryPage>

      {/* Wave E: ONE unified editor for all actor types (AC-EDIT-1 finalize)
          Milestone A (AC-ACTORS-1/2): initialActorType pre-scopes the editor; no type-picker shown */}
      {editorTarget !== null && domainsQuery.data && (
        <UnifiedActorEditor
          isOpen={editorTarget !== null}
          onClose={() => setEditorTarget(null)}
          actor={editorTarget.actor ?? undefined}
          agentDefinition={editorTarget.agentDefinition ?? undefined}
          domains={domainsQuery.data}
          initialActorType={editorTarget.initialActorType}
        />
      )}

      <ConfirmDialog
        isOpen={!!deactivateActor}
        onClose={() => setDeactivateActor(null)}
        onConfirm={handleDeactivateConfirm}
        title={`${t('Deactivate')} ${deactivateActor?.name ?? t('Actor')}?`}
        description={t('Deactivating this actor will prevent them from being assigned to new work items. Active work items will need to be reassigned.')}
        confirmText={t('Deactivate')}
        variant="destructive"
        isLoading={deactivateMutation.isPending}
      />

      <ConfirmDialog
        isOpen={!!archiveActor}
        onClose={() => setArchiveActor(null)}
        onConfirm={handleArchiveConfirm}
        title={t('Delete this actor?')}
        description={archiveActor ? `"${archiveActor.name}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={archiveMutation.isPending}
      />
    </div>
  );
}

/** Legacy page wrapper — the /roros/actors route still uses this. */
export function ActorsPage() {
  return (
    <RorosPage
      title={t('Actors')}
      icon={<Users className="h-5 w-5" />}
    >
      <ActorsPanel />
    </RorosPage>
  );
}
